/**
 * Admin → teacher/student dashboard handoff.
 *
 * Login stores jwtToken on .artiumacademy.com AND authToken in localStorage.
 * Logging out of betadashboard clears the shared-domain cookie but leaves
 * admin authToken intact. Before opening View Dashboard / View Chat, restore
 * the cookie from authToken when missing so the destination site can SSO.
 */

const JWT_COOKIE_NAME = "jwtToken";
const COOKIE_DOMAIN = ".artiumacademy.com";

export const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};

/** Same cookie shape as Authentication/Login.tsx */
export const setJwtTokenCookie = (token: string): void => {
  if (typeof document === "undefined" || !token) return;
  const expirationDate = new Date();
  expirationDate.setFullYear(expirationDate.getFullYear() + 1);
  const cookieValue = encodeURIComponent(token);
  const cookieOptions = `expires=${expirationDate.toUTCString()}; domain=${COOKIE_DOMAIN};path=/`;
  document.cookie = `${JWT_COOKIE_NAME}=${cookieValue}; ${cookieOptions}`;
};

/** Token usable for dashboard SSO: cookie first, else admin localStorage authToken. */
export const getAdminAuthToken = (): string => {
  const fromCookie = getCookie(JWT_COOKIE_NAME);
  if (fromCookie) {
    try {
      return decodeURIComponent(fromCookie);
    } catch {
      return fromCookie;
    }
  }
  return String(localStorage.getItem("authToken") || "").trim();
};

/**
 * Ensure jwtToken cookie exists for .artiumacademy.com.
 * Returns the token if available, otherwise empty string.
 */
export const ensureJwtCookieForDashboard = (): string => {
  const existing = getCookie(JWT_COOKIE_NAME);
  if (existing) {
    try {
      return decodeURIComponent(existing);
    } catch {
      return existing;
    }
  }

  const authToken = String(localStorage.getItem("authToken") || "").trim();
  if (!authToken) return "";

  setJwtTokenCookie(authToken);
  return authToken;
};

/**
 * Restore cookie if needed, then open URL in a new tab.
 * Returns false if there is no admin token to hand off.
 */
export const openDashboardWithAuth = (url: string): boolean => {
  const token = ensureJwtCookieForDashboard();
  if (!token) return false;
  window.open(url, "_blank", "noopener,noreferrer");
  return true;
};
